import React, { useEffect, useState, useRef } from "react";
import api from "../api";

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 12; // 36 seconds total

const PageShell = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#f4f7fe", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 16px" }}>
    {children}
  </div>
);

export default function CheckoutSuccess() {
  const [reportStatus, setReportStatus] = useState("processing"); // "processing" | "ready" | "timeout" | "expired"
  const [reportUrl, setReportUrl] = useState(null);
  const [vin, setVin] = useState("");
  const [email, setEmail] = useState(null);
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    document.title = "Payment Successful | VehicleHistoryNow";

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setReportStatus("timeout");
      return;
    }

    const poll = async () => {
      attemptRef.current += 1;
      try {
        const res = await api.get(`/api/report/session/${sessionId}/`);

        if (res.data.status === "ready") {
          setReportUrl(res.data.pdf_url);
          setVin(res.data.vin || "");
          setEmail(res.data.email || null);
          setReportStatus("ready");
          return;
        }
      } catch (err) {
        if (err.response?.status === 410) {
          setReportStatus("expired");
          return;
        }
        // 202 or transient network error — keep polling
      }

      if (attemptRef.current >= MAX_ATTEMPTS) {
        setReportStatus("timeout");
        return;
      }

      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // ── Processing ──────────────────────────────────────────────────────────────
  if (reportStatus === "processing") {
    return (
      <PageShell>
        {/* Grid / data logo — represents "analyzing data" */}
        <img src="/vhn-logo-grid.svg" alt="Vehicle History Now" height="60" style={{ marginBottom: "28px" }} />
        <div style={{ background: "#fff", borderRadius: "16px", padding: "36px 40px", textAlign: "center", maxWidth: "420px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize: "36px", marginBottom: "14px" }}>⏳</div>
          <h2 style={{ margin: "0 0 10px", fontSize: "22px", color: "#0d2137" }}>Payment Successful!</h2>
          <p style={{ margin: 0, color: "#555", fontSize: "15px", lineHeight: 1.6 }}>
            Generating your vehicle history report — this usually takes just a few seconds…
          </p>
          {/* Pulsing dots */}
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "8px" }}>
            {[0, 0.2, 0.4].map((delay, i) => (
              <div key={i} style={{
                width: "10px", height: "10px", borderRadius: "50%",
                background: "#00b8b8",
                animation: `pulse 1.2s ${delay}s ease-in-out infinite`,
              }} />
            ))}
          </div>
          <style>{`@keyframes pulse { 0%,100%{opacity:.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }`}</style>
        </div>
      </PageShell>
    );
  }

  // ── Expired ─────────────────────────────────────────────────────────────────
  if (reportStatus === "expired") {
    return (
      <PageShell>
        <img src="/vhn-logo-car.svg" alt="Vehicle History Now" height="44" style={{ marginBottom: "24px" }} />
        <div style={{ background: "#fff", borderRadius: "16px", padding: "36px 40px", textAlign: "center", maxWidth: "420px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <h2 style={{ margin: "0 0 10px", fontSize: "22px", color: "#0d2137" }}>Report Link Expired</h2>
          <p style={{ margin: 0, color: "#555" }}>
            This report link is no longer valid. Please{" "}
            <a href="mailto:support@vehiclehistorynow.com" style={{ color: "#1a73e8" }}>contact support</a>{" "}
            if you need assistance.
          </p>
        </div>
      </PageShell>
    );
  }

  // ── Timeout ─────────────────────────────────────────────────────────────────
  if (reportStatus === "timeout") {
    return (
      <PageShell>
        <img src="/vhn-logo-car.svg" alt="Vehicle History Now" height="44" style={{ marginBottom: "24px" }} />
        <div style={{ background: "#fff", borderRadius: "16px", padding: "36px 40px", textAlign: "center", maxWidth: "420px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <h2 style={{ margin: "0 0 10px", fontSize: "22px", color: "#0d2137" }}>Payment Successful!</h2>
          <p style={{ margin: "0 0 12px", color: "#555" }}>Your report is taking a little longer than expected to generate.</p>
          {email ? (
            <p style={{ margin: 0, color: "#555" }}>
              We'll email it to <strong>{email}</strong> as soon as it's ready.
            </p>
          ) : (
            <p style={{ margin: 0, color: "#555" }}>
              Please refresh this page in a moment, or{" "}
              <a href="mailto:support@vehiclehistorynow.com" style={{ color: "#1a73e8" }}>contact support</a>{" "}
              if the issue persists.
            </p>
          )}
        </div>
      </PageShell>
    );
  }

  // ── Ready ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#f4f7fe", minHeight: "100vh" }}>
      {/* Top bar with checkmark logo */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e8eaed",
        padding: "14px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
      }}>
        <img src="/vhn-logo-check.svg" alt="Vehicle History Now" height="40" />
        <span style={{ fontSize: "13px", color: "#888" }}>
          <a href="/" style={{ color: "#1a73e8", textDecoration: "none" }}>← Back to Home</a>
        </span>
      </div>

      {/* Report info bar */}
      <div style={{ textAlign: "center", padding: "24px 16px 12px" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: "22px", color: "#0d2137" }}>
          ✅ Your Vehicle History Report is Ready!
        </h2>
        {vin && (
          <p style={{ margin: "0 0 4px", color: "#555", fontSize: "14px" }}>
            VIN: <strong style={{ fontFamily: "monospace", letterSpacing: "1px" }}>{vin}</strong>
          </p>
        )}
        {email && (
          <p style={{ margin: "0 0 10px", color: "#555", fontSize: "14px" }}>
            A copy has been sent to <strong>{email}</strong>
          </p>
        )}
        <a
          href={reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            marginTop: "6px",
            padding: "10px 24px",
            background: "#1a73e8",
            color: "#fff",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          Open Report in New Tab ↗
        </a>
      </div>

      <iframe
        src={reportUrl}
        title="Vehicle History Report"
        style={{
          display: "block",
          width: "100%",
          height: "78vh",
          border: "none",
          borderTop: "1px solid #ddd",
        }}
      />
    </div>
  );
}
